import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/livestream` : 'https://backend-c-0mxm.onrender.com/api/livestream';

// Tạo instance axios với cấu hình mặc định
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Luôn gắn token cho MỌI request (giống interceptor của app mobile).
// Trước đây chỉ vài hàm admin tự thêm getAuthHeader() nên 17 chỗ gọi không có token —
// backend đành để ngỏ, ai biết URL là đọc/sửa được lương. Gắn ở đây để bật xác thực
// phía server mà không vỡ màn nào.
api.interceptors.request.use((config) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // userId TRƯỚC: backend coi Bearer là userId thô (User.findById), không phải JWT
    const token = user?.userId || user?.token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) { /* chưa đăng nhập → để server từ chối */ }
  return config;
});

// Hàm xử lý lỗi chung
const handleError = (error) => {
  if (error.response) {
    // Server trả về lỗi với status code
    return {
      message: error.response.data.message || 'Có lỗi xảy ra từ server',
      status: error.response.status,
    };
  } else if (error.request) {
    // Không nhận được response từ server
    return {
      message: 'Không thể kết nối đến server',
      status: 0,
    };
  } else {
    // Lỗi khi tạo request
    return {
      message: 'Có lỗi xảy ra khi tạo request',
      status: 0,
    };
  }
};

// Gửi dữ liệu livestream mới
export const submitLivestream = async (data) => {
  try {
    const response = await api.post('/', data);
    
    // Đảm bảo dữ liệu trả về hợp lệ
    if (response.data && response.data._id) {
      // Trả về dữ liệu đầy đủ nếu server trả về bản ghi đầy đủ
      return {
        success: true,
        data: response.data,
      };
    } else if (response.data && response.data.data) {
      // Trường hợp server trả về dữ liệu trong trường data
      return {
        success: true,
        data: response.data.data,
      };
    } else {
      // Trường hợp không có dữ liệu đầy đủ, tự tạo đối tượng
      return {
        success: true,
        data: {
          _id: Date.now().toString(), // ID tạm thời
          ...data,
          createdAt: new Date().toISOString()
        },
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// Lấy tổng hợp theo tháng
export const getMonthlySummary = async (userId, year, month) => {
  try {
    const response = await api.get(`/monthly/${userId}/${year}/${month}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// Lấy tổng hợp theo tuần
export const getWeeklySummary = async (userId, year, week) => {
  try {
    const response = await api.get(`/weekly/${userId}/${year}/${week}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// API mới cho admin
export const getAllLivestreams = async () => {
  try {
    const response = await api.get('/all?limit=1000');
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const updateLivestream = async (id, data) => {
  try {
    const response = await api.put(`/${id}`, data);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const deleteLivestream = async (id) => {
  try {
    const response = await api.delete(`/${id}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// API mới cho thống kê
export const getCustomRangeSummary = async (userId, startDate, endDate) => {
  try {
    const response = await api.get(`/summary/${userId}`, {
      params: {
        startDate,
        endDate
      }
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const getUserSummaries = async () => {
  try {
    const response = await api.get('/summaries');
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const getUserSummary = async (userId) => {
  try {
    const response = await api.get(`/user-summary/${userId}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// Lưu tổng kết kỳ
export const savePeriodSummary = async () => {
  try {
    const response = await api.post('/save-period-summary');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Lưu tổng kết kỳ thủ công cho admin
export const adminSavePeriodSummary = async (periodType = 'completed') => {
  try {
    const response = await api.post('/admin/save-period-summary', {
      periodType // 'completed' (kỳ vừa kết thúc) hoặc 'current' (kỳ hiện tại)
    });
    return {
      success: true,
      data: response.data.data,
      message: response.data.message,
      period: response.data.period
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// ====== PERIOD BONUS (Thưởng cuối kỳ) ======
const getAuthHeader = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId ? { Authorization: `Bearer ${user.userId}` } : {};
  } catch (e) {
    return {};
  }
};

// [ADMIN] Lấy toàn bộ bonus cho tab quản lý
export const getAllBonuses = async () => {
  try {
    const response = await api.get('/admin/bonuses', { headers: getAuthHeader() });
    return { success: true, data: response.data?.data || [] };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Thêm bonus
export const addBonus = async (summaryId, payload) => {
  try {
    const response = await api.post(`/admin/period-summaries/${summaryId}/bonuses`, payload, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Sửa bonus
export const updateBonus = async (summaryId, bonusId, payload) => {
  try {
    const response = await api.put(`/admin/period-summaries/${summaryId}/bonuses/${bonusId}`, payload, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Xoá bonus (soft)
export const deleteBonus = async (summaryId, bonusId) => {
  try {
    const response = await api.delete(`/admin/period-summaries/${summaryId}/bonuses/${bonusId}`, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Thêm khấu trừ thủ công (trễ ca, vắng, khung trống...)
export const addDeduction = async (summaryId, payload) => {
  try {
    const response = await api.post(`/admin/period-summaries/${summaryId}/deductions`, payload, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Sửa khấu trừ thủ công
export const updateDeduction = async (summaryId, deductionId, payload) => {
  try {
    const response = await api.put(`/admin/period-summaries/${summaryId}/deductions/${deductionId}`, payload, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Xoá khấu trừ thủ công
export const deleteDeduction = async (summaryId, deductionId) => {
  try {
    const response = await api.delete(`/admin/period-summaries/${summaryId}/deductions/${deductionId}`, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Điều chỉnh 1 khoản thưởng tự động của kỳ. amount = 0 → bỏ hẳn khoản đó.
// Điều chỉnh được lưu riêng nên không bị mất khi hệ thống chốt lại kỳ.
export const adjustAutoBonus = async (summaryId, type, payload) => {
  try {
    const response = await api.put(`/admin/period-summaries/${summaryId}/auto-bonuses/${encodeURIComponent(type)}`, payload, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data, message: response.data?.message };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// [ADMIN] Gỡ điều chỉnh → khoản thưởng quay về số hệ thống tính
export const resetAutoBonus = async (summaryId, type) => {
  try {
    const response = await api.delete(`/admin/period-summaries/${summaryId}/auto-bonuses/${encodeURIComponent(type)}`, { headers: getAuthHeader() });
    return { success: true, data: response.data?.data, message: response.data?.message };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// Lấy tổng kết các kỳ trước
export const getPeriodSummaries = async (period, userId) => {
  try {
    // Kiểm tra nếu không có userId hoặc userId không phải ObjectId hợp lệ
    if (!userId || userId === 'current' || userId === 'previous') {
      // Gọi API không có userId cụ thể
      const response = await api.get(`/period-summaries`);
      return {
        success: true,
        data: response.data
      };
    }
    
    // Gọi API với userId cụ thể
    const response = await api.get(`/period-summaries/${userId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Gửi yêu cầu ứng tiền
export const requestAdvancePayment = async (data) => {
  try {
    const response = await api.post('/advance-payment', data);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error requesting advance payment:', error);
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Lấy lịch sử ứng tiền của user
export const getAdvancePaymentHistory = async (userId) => {
  try {
    const response = await api.get(`/advance-payment/${userId}`);
    return {
      success: true,
      data: response.data.data
    };
  } catch (error) {
    console.error('Error getting advance payment history:', error);
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Hủy yêu cầu ứng tiền
export const cancelAdvancePayment = async (id) => {
  try {
    const response = await api.delete(`/advance-payment/${id}`);
    return {
      success: true,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error canceling advance payment:', error);
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Lấy toàn bộ lịch sử chấm công của user với phân trang và lọc
export const getLivestreamHistory = async (userId, options = {}) => {
  try {
    const {
      page = 1,
      limit = 20,
      startDate,
      endDate,
      sortBy = 'date',
      sortOrder = 'desc'
    } = options;
    
    const params = {
      page,
      limit,
      sortBy,
      sortOrder
    };
    
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await api.get(`/history/${userId}`, { params });
    return {
      success: true,
      data: response.data.data
    };
  } catch (error) {
    console.error('Error fetching livestream history:', error);
    return {
      success: false,
      error: handleError(error)
    };
  }
}; 